import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Cloud } from 'lucide-react';
import composioService from '../../services/composioService';
import triggerService from '../../services/triggerService';
import {
  getConnectionAccountId,
  getConnectionDisplayName,
  getMergedIngestSetupFields,
  getTriggerTypeEventName,
  getTriggerTypeLabel,
  getTriggerTypeSlug,
  normalizeTriggerTypesList,
  extractIngestSchemaFieldsForEvent,
} from '../../utils/composioTrigger';
import { isLikelyPollingTrigger } from '../../utils/composioWebhook';
import FolderIdHelpHint from '../shared/FolderIdHelpHint';
import IngestDriveQueryField from '../shared/IngestDriveQueryField';
import {
  apiErrMessage,
  buildGoogleDriveSyncTriggerName,
  buildIngestTriggerConfigForSlug,
  createIngestFormState,
  FILE_INGEST_TOOLKIT_LABELS,
  filterSlugsForIngestField,
  formatEventSlug,
  listGoogleDriveConnections,
  pickGoogleDriveFileCreatedSlug,
  readMergedIngestFieldValue,
  writeMergedIngestFieldValue,
} from './triggerFormUtils';
import styles from './GoogleDriveSyncDialog.module.scss';

const SELECT_PROPS = { MenuProps: { PaperProps: { className: styles.menuPaper } } };

const GoogleDriveSyncDialog = ({
  open,
  onClose,
  onSuccess,
  dim3FolderId,
  dim3FolderName = '',
}) => {
  const [form, setForm] = useState(() => createIngestFormState(dim3FolderId));
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [driveConnections, setDriveConnections] = useState([]);
  const [composioTriggerTypes, setComposioTriggerTypes] = useState([]);
  const [composioTypeSchemas, setComposioTypeSchemas] = useState({});
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingComposioSchema, setLoadingComposioSchema] = useState(false);

  const folderLabel = dim3FolderName || `Folder #${dim3FolderId}`;

  const loadComposioTriggerSchemas = useCallback(async (slugs = []) => {
    const normalized = slugs.map((s) => String(s).trim()).filter(Boolean);
    if (!normalized.length) {
      setComposioTypeSchemas({});
      return;
    }
    setLoadingComposioSchema(true);
    try {
      const results = await Promise.all(
        normalized.map((slug) => composioService.getTriggerType(slug)),
      );
      const nextSchemas = {};
      normalized.forEach((slug, index) => {
        nextSchemas[slug] = results[index];
      });
      setComposioTypeSchemas(nextSchemas);

      setForm((prev) => {
        const prevByEvent = prev.composio_config_by_event || {};
        const nextByEvent = { ...prevByEvent };
        normalized.forEach((slug, index) => {
          const setupFields = extractIngestSchemaFieldsForEvent(results[index], slug);
          const existing = { ...(nextByEvent[slug] || {}) };
          setupFields.forEach((field) => {
            if (existing[field.key] === undefined && field.default !== undefined) {
              existing[field.key] = field.default;
            }
          });
          nextByEvent[slug] = existing;
        });
        Object.keys(nextByEvent).forEach((slug) => {
          if (!normalized.includes(slug)) delete nextByEvent[slug];
        });
        return { ...prev, composio_config_by_event: nextByEvent };
      });
    } catch (e) {
      console.error('Failed loading trigger type schemas:', e);
      setComposioTypeSchemas({});
    } finally {
      setLoadingComposioSchema(false);
    }
  }, []);

  const initializeDialog = useCallback(async () => {
    setLoadingInit(true);
    setFormError('');
    setDriveConnections([]);
    setComposioTriggerTypes([]);
    setComposioTypeSchemas({});

    const baseForm = {
      ...createIngestFormState(dim3FolderId),
      name: buildGoogleDriveSyncTriggerName(dim3FolderName, dim3FolderId),
    };
    setForm(baseForm);

    try {
      const res = await composioService.getConnectedApps();
      const list = res?.connections || res?.connected_apps || [];
      const driveConns = listGoogleDriveConnections(list);
      setDriveConnections(driveConns);
      if (!driveConns.length) {
        return;
      }

      const defaultConn = driveConns[0];
      const accountId = getConnectionAccountId(defaultConn);

      const typesData = await composioService.listTriggerTypes('googledrive');
      const triggerTypes = normalizeTriggerTypesList(typesData);
      setComposioTriggerTypes(triggerTypes);
      const defaultSlug = pickGoogleDriveFileCreatedSlug(triggerTypes);

      setForm((prev) => ({
        ...prev,
        connected_account_id: accountId,
        composio_trigger_slug: defaultSlug,
        composio_trigger_slugs: defaultSlug ? [defaultSlug] : [],
        name: buildGoogleDriveSyncTriggerName(dim3FolderName, dim3FolderId),
      }));
    } catch (e) {
      console.error('Failed initializing Google Drive sync dialog:', e);
      setFormError(apiErrMessage(e, 'Failed to load connected accounts.'));
    } finally {
      setLoadingInit(false);
    }
  }, [dim3FolderId, dim3FolderName]);

  useEffect(() => {
    if (!open) return;
    initializeDialog();
  }, [open, initializeDialog]);

  const selectedDriveConnection = useMemo(() => {
    if (!driveConnections.length) return null;
    const selectedId = String(form.connected_account_id || '').trim();
    if (selectedId) {
      const match = driveConnections.find(
        (conn) => getConnectionAccountId(conn) === selectedId,
      );
      if (match) return match;
    }
    return driveConnections[0];
  }, [driveConnections, form.connected_account_id]);

  const hasDriveConnections = driveConnections.length > 0;
  const showAccountPicker = driveConnections.length > 1;

  const composioEventLabelMap = useMemo(() => {
    const map = new Map();
    composioTriggerTypes.forEach((item) => {
      const slug = getTriggerTypeSlug(item);
      if (slug) map.set(slug, getTriggerTypeLabel(item));
    });
    return map;
  }, [composioTriggerTypes]);

  const selectedIngestSlugs = useMemo(
    () => (form.composio_trigger_slugs || []).map((s) => String(s).trim()).filter(Boolean),
    [form.composio_trigger_slugs],
  );

  const { mergedIngestSetupFields, querySetupField } = useMemo(() => {
    const all = getMergedIngestSetupFields(composioTypeSchemas);
    return {
      mergedIngestSetupFields: all.filter(
        ({ field }) => field.key !== 'folder_id' && field.key !== 'query',
      ),
      querySetupField: all.find(({ field }) => field.key === 'query'),
    };
  }, [composioTypeSchemas]);

  useEffect(() => {
    if (!open || loadingInit) return;
    if (!selectedIngestSlugs.length) {
      setComposioTypeSchemas({});
      return;
    }
    loadComposioTriggerSchemas(selectedIngestSlugs);
  }, [open, loadingInit, selectedIngestSlugs, loadComposioTriggerSchemas]);

  const folderIdSlugs = useMemo(
    () => filterSlugsForIngestField(composioTypeSchemas, selectedIngestSlugs, 'folder_id'),
    [composioTypeSchemas, selectedIngestSlugs],
  );

  const folderIdFilled = useMemo(() => {
    const value = form.drive_folder_id;
    return value !== undefined && String(value).trim() !== '';
  }, [form.drive_folder_id]);

  const handleFolderIdChange = (value) => {
    setForm((p) => ({
      ...p,
      drive_folder_id: value,
      composio_config_by_event: writeMergedIngestFieldValue(
        p.composio_config_by_event,
        folderIdSlugs,
        'folder_id',
        value,
      ),
    }));
  };

  const updateSelectedIngestSlugs = useCallback(
    (slugs) => {
      setForm((p) => {
        const prevByEvent = p.composio_config_by_event || {};
        const nextByEvent = {};
        const nextFolderIdSlugs = filterSlugsForIngestField(composioTypeSchemas, slugs, 'folder_id');
        slugs.forEach((slug) => {
          nextByEvent[slug] = {
            ...(prevByEvent[slug] || {}),
            ...(p.drive_folder_id && nextFolderIdSlugs.includes(slug)
              ? { folder_id: p.drive_folder_id }
              : {}),
          };
        });
        return {
          ...p,
          composio_trigger_slugs: slugs,
          composio_trigger_slug: slugs[0] || '',
          composio_config_by_event: nextByEvent,
        };
      });
    },
    [composioTypeSchemas],
  );

  useEffect(() => {
    if (!folderIdFilled && form.mirror_nested) {
      setForm((p) => ({ ...p, mirror_nested: false }));
    }
  }, [folderIdFilled, form.mirror_nested]);

  const handleSave = async () => {
    try {
      setFormError('');
      if (!selectedDriveConnection || !form.connected_account_id) {
        throw new Error(
          `Connect a supported storage app (${FILE_INGEST_TOOLKIT_LABELS.join(', ')}) on the Tools page first.`,
        );
      }
      if (!form.dim3_folder_id) throw new Error('Target folder is missing.');
      const slugs = selectedIngestSlugs;
      if (!slugs.length) throw new Error('Select at least one event type.');

      if (loadingComposioSchema || loadingInit) {
        throw new Error('Still loading configuration. Please wait.');
      }
      const missingSchema = slugs.filter((slug) => !composioTypeSchemas[slug]);
      if (missingSchema.length > 0) {
        throw new Error('Event configuration is still loading. Please wait and try again.');
      }

      const baseName =
        form.name.trim() || buildGoogleDriveSyncTriggerName(dim3FolderName, dim3FolderId);
      const configByEvent = { ...(form.composio_config_by_event || {}) };
      if (form.drive_folder_id) {
        folderIdSlugs.forEach((slug) => {
          configByEvent[slug] = {
            ...(configByEvent[slug] || {}),
            folder_id: form.drive_folder_id,
          };
        });
      }
      const formWithName = { ...form, name: baseName, composio_config_by_event: configByEvent };

      setSubmitting(true);
      for (const slug of slugs) {
        const trigger_config = buildIngestTriggerConfigForSlug(
          formWithName,
          slug,
          composioTypeSchemas,
          configByEvent,
        );
        const eventName = getTriggerTypeEventName(
          composioTypeSchemas[slug],
          composioEventLabelMap.get(slug) || formatEventSlug(slug),
        );
        await triggerService.createTrigger({
          agent_id: null,
          name: slugs.length > 1 ? `${baseName} — ${eventName}` : baseName,
          trigger_type: 'composio',
          query_template: '',
          trigger_config,
        });
      }

      onSuccess?.();
      onClose?.();
    } catch (e) {
      setFormError(e.message || apiErrMessage(e, 'Failed to start cloud storage sync.'));
    } finally {
      setSubmitting(false);
    }
  };

  const busy = loadingInit || loadingComposioSchema || submitting;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      fullWidth
      maxWidth="sm"
      classes={{ paper: styles.dialogPaper }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <span className={styles.titleIcon} aria-hidden>
          <Cloud size={18} strokeWidth={2} />
        </span>
        {hasDriveConnections ? 'Sync from Google Drive' : 'Sync files'}
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {formError && <Alert severity="error">{formError}</Alert>}

        {!loadingInit && !hasDriveConnections && (
          <Alert severity="info" className={styles.folderBanner}>
            No compatible storage account connected. Connect a supported storage app (
            {FILE_INGEST_TOOLKIT_LABELS.join(', ')}) on the Tools page first.
          </Alert>
        )}

        {loadingInit ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          hasDriveConnections && (
            <>
              {showAccountPicker ? (
                <TextField
                  select
                  label="Connected account"
                  value={form.connected_account_id || getConnectionAccountId(driveConnections[0])}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, connected_account_id: e.target.value }))
                  }
                  required
                  fullWidth
                  size="small"
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={SELECT_PROPS}
                >
                  {driveConnections.map((conn) => {
                    const id = getConnectionAccountId(conn);
                    return (
                      <MenuItem key={id} value={id}>
                        {getConnectionDisplayName(conn)}
                      </MenuItem>
                    );
                  })}
                </TextField>
              ) : (
                <Typography variant="body2" color="text.secondary" className={styles.accountHint}>
                  Connected account:{' '}
                  <strong>{getConnectionDisplayName(selectedDriveConnection)}</strong>
                </Typography>
              )}

              <Box className={styles.fieldBlock}>
                <Box className={styles.fieldLabelRow}>
                  <Typography component="label" className={styles.fieldLabel}>
                    Folder Id
                  </Typography>
                  <FolderIdHelpHint />
                </Box>
                <TextField
                  value={form.drive_folder_id ?? ''}
                  onChange={(e) => handleFolderIdChange(e.target.value)}
                  fullWidth
                  size="small"
                  className={styles.formField}
                  disabled={loadingComposioSchema}
                  aria-label="Folder Id"
                />
              </Box>

              <TextField
                select
                label="Event type"
                value={selectedIngestSlugs}
                onChange={(e) => {
                  const value = e.target.value;
                  const slugs = typeof value === 'string' ? value.split(',') : value;
                  updateSelectedIngestSlugs(slugs);
                }}
                required
                fullWidth
                size="small"
                className={styles.formField}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  ...SELECT_PROPS,
                  multiple: true,
                  renderValue: (selected) => (
                    <Box className={styles.eventTypeChips}>
                      {selected.map((slug) => (
                        <Chip
                          key={slug}
                          label={composioEventLabelMap.get(slug) || formatEventSlug(slug)}
                          size="small"
                          className={styles.eventTypeChip}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={(e) => {
                            e.stopPropagation();
                            updateSelectedIngestSlugs(selected.filter((s) => s !== slug));
                          }}
                        />
                      ))}
                    </Box>
                  ),
                }}
              >
                {composioTriggerTypes.map((item) => {
                  const slug = getTriggerTypeSlug(item);
                  return (
                    <MenuItem key={slug} value={slug}>
                      {getTriggerTypeLabel(item)}
                    </MenuItem>
                  );
                })}
              </TextField>

              {selectedIngestSlugs.some((slug) => isLikelyPollingTrigger(slug)) && (
                <Typography variant="caption" color="text.secondary" className={styles.pollHint}>
                  Poll-based — new files may take up to 15 minutes to appear.
                </Typography>
              )}

              {querySetupField && (
                <IngestDriveQueryField
                  field={querySetupField.field}
                  value={readMergedIngestFieldValue(
                    form.composio_config_by_event,
                    querySetupField.slugs,
                    'query',
                  )}
                  onChange={(value) => {
                    setForm((p) => ({
                      ...p,
                      composio_config_by_event: writeMergedIngestFieldValue(
                        p.composio_config_by_event,
                        querySetupField.slugs,
                        'query',
                        value,
                      ),
                    }));
                  }}
                  disabled={loadingComposioSchema}
                />
              )}

              {mergedIngestSetupFields.map(({ field, slugs }) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  value={readMergedIngestFieldValue(
                    form.composio_config_by_event,
                    slugs,
                    field.key,
                  )}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((p) => ({
                      ...p,
                      composio_config_by_event: writeMergedIngestFieldValue(
                        p.composio_config_by_event,
                        slugs,
                        field.key,
                        value,
                      ),
                    }));
                  }}
                  fullWidth
                  size="small"
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  disabled={loadingComposioSchema}
                />
              ))}

              {folderIdFilled && (
                <>
                  <FormControlLabel
                    className={styles.mirrorSwitch}
                    control={
                      <Switch
                        checked={form.mirror_nested}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, mirror_nested: e.target.checked }))
                        }
                        color="primary"
                      />
                    }
                    label="Include nested folders"
                  />
                  <Typography variant="caption" color="text.secondary" className={styles.hint}>
                    Mirror subfolders from the watched Drive folder into <strong>{folderLabel}</strong>.
                  </Typography>
                </>
              )}
            </>
          )
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button onClick={onClose} disabled={busy} className={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={busy || !hasDriveConnections || !form.connected_account_id}
          className={styles.syncButton}
          variant="contained"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Start sync'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleDriveSyncDialog;
